import React, { useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';

import { useEffectAfterMount } from '@crehana/react-hooks';

import { useAppContext } from '../../context/AppContext';
import CATALOG_PAGE_SEO_QUERY from '../../graphql/CatalogPageSeo.graphql';
import type {
  CatalogPageSeo,
  CatalogPageSeoVariables,
} from '../../graphql/types/CatalogPageSeo';

import BreadcrumpSEO from './BreadcrumpSEO';
import { getBreadcrumbs } from './utils';

const Breadcrumbs: React.FC<{
  trackPageRender(category: string, subCategory: string): void;
  getLongDescription: (value: string | null) => void;
}> = ({ trackPageRender, getLongDescription }) => {
  const { filters, orderBy, filterActions, orderByActions } = useAppContext();
  const [getSeoData, { data }] = useLazyQuery<
    CatalogPageSeo,
    CatalogPageSeoVariables
  >(CATALOG_PAGE_SEO_QUERY);

  const breadcrumbs = getBreadcrumbs({ filters, orderBy });

  const courses = breadcrumbs.get('courses');
  const category = breadcrumbs.get('category');
  const subCategory = breadcrumbs.get('subCategory');

  useEffect(() => {
    trackPageRender(
      category ? category.name! : 'null',
      subCategory ? subCategory.name! : 'null',
    );
  }, []);

  // This hook doesn't run on the first render, only when the dependencies update
  useEffectAfterMount(() => {
    getSeoData({
      variables: {
        category: filters.categorySelected.slug,
        orderBy: orderBy.itemSelected.slug,
      },
    });
  }, [filters.categorySelected.slug, orderBy.itemSelected.slug]);

  useEffectAfterMount(() => {
    if (getLongDescription) {
      getLongDescription(data?.pageSetup?.seo?.longDescription!);
    }
  }, [data?.pageSetup?.seo?.longDescription]);

  // Don't update the meta title on first render, only when there's a page transition.
  const metaTitle = data?.pageSetup?.seo?.title
    ? `${data.pageSetup.seo.title} | Crehana`
    : undefined;
  const metaDescription = data?.pageSetup?.seo?.description;

  useEffect(() => {
    const titleElement = document.querySelector('meta[name="title"]');
    const descriptionElement = document.querySelector(
      'meta[name="description"]',
    );

    if (metaTitle && titleElement) {
      titleElement.setAttribute('content', metaTitle);
    }
    if (metaDescription && descriptionElement) {
      descriptionElement.setAttribute('content', metaDescription);
    }
  }, [metaTitle, metaDescription]);

  const getBreadcrumbsWithOnClickHandlers = () => {
    breadcrumbs.set('courses', {
      ...courses,
      onClick: () => {
        // remove both filters and orderBy
        orderByActions.setDefaultOrderBy();
        filterActions.clearAllFilters();
        // @ts-expect-error
        if (window.clearTextValue) window.clearTextValue();
      },
    });

    if (category) {
      breadcrumbs.set('category', {
        ...category,
        // subItemId to zero because it is the "Todos <category name>" option
        onClick: () => {
          filterActions.toggleFilterCategory({
            itemId: category.id as string,
            slug: category.slug!,
            subItemId: 0,
          });
        },
      });
    }

    if (subCategory) {
      breadcrumbs.set('subCategory', {
        ...subCategory,
        onClick: orderByActions.setDefaultOrderBy,
      });
    }

    return Array.from(breadcrumbs.values());
  };

  const breadCrumbsItems = getBreadcrumbsWithOnClickHandlers();

  return (
    <div className="tw-font-pop w-full mt-24 mb-12 sm:mb-32 md:mt-32 md:mb-32 m-auto">
      {/* @ts-expect-error */}
      <BreadcrumpSEO breadcrumbs={breadCrumbsItems} title={metaTitle} />
    </div>
  );
};

export default Breadcrumbs;
